import React from 'react';
import { ArrowRight, CheckmarkHeavy } from '@1debit/icons';
import { cn } from 'utils/styles';
import { BulletListStyle, BulletStyle } from 'types/constants';

/**
 * Maps Contentful's BulletStyle to RichTextCopy's BulletListStyle.
 * @param bulletStyle - The bullet style from Contentful
 * @returns The corresponding BulletListStyle for RichTextCopy, defaulting to Arrow
 */
export const getBulletListStyle = (bulletStyle: BulletStyle | undefined): BulletListStyle => {
  if (bulletStyle === BulletStyle.CheckBoxes) {
    return BulletListStyle.Checkmark;
  }
  return BulletListStyle.Arrow;
};

// Helpers for list styling
const baseListClasses = cn('align-left ml-0');
const listDiscClasses = 'list-disc pl-5';
const listDecimalClasses = 'list-decimal pl-5';

// Common spacing classes
const getListItemSpacing = (isInline: boolean) => (isInline ? 'first:mt-0 last:mb-0' : 'my-2');

const getNestedListClasses = (listType: 'ul' | 'ol') => (listType === 'ul' ? '[&_ul]:pl-10' : '[&_ol]:pl-10');

export const getUnorderedListClasses = (
  showSimpleListStyle: boolean = false,
  bulletStyle?: BulletListStyle,
) => {
  if (showSimpleListStyle) {
    return `${listDiscClasses} ${getNestedListClasses('ul')}`;
  }
  // Bullet style uses native list markers
  if (bulletStyle === BulletListStyle.Bullet) {
    return cn(listDiscClasses, baseListClasses);
  }
  // Dash style uses custom flex layout, no list markers needed
  if (bulletStyle === BulletListStyle.Dash) {
    return cn('list-none', baseListClasses);
  }
  // Only use flex for icon-based bullets (arrow, checkmark)
  return cn('[&_li]:flex [&_li]:items-start', baseListClasses);
};

export const getOrderedListClasses = (showSimpleListStyle: boolean = false) => {
  return showSimpleListStyle
    ? `${listDecimalClasses} ${getNestedListClasses('ol')}`
    : cn(listDecimalClasses, '[&_.arrow-right]:hidden [&_.checkmark-bullet]:hidden', baseListClasses);
};

export const renderListItem = (
  children: React.ReactNode,
  showSimpleListStyle: boolean = false,
  arrowStyles: string = '',
  bulletStyle: BulletListStyle = BulletListStyle.Arrow,
  isInline: boolean = false,
) => {
  const spacingClasses = getListItemSpacing(isInline);

  if (showSimpleListStyle) {
    return <li className="my-2">{children}</li>;
  }

  // Handle custom bullet styles that return early
  if (bulletStyle === BulletListStyle.Bullet) {
    return <li className={cn(spacingClasses)}>{children}</li>;
  }

  if (bulletStyle === BulletListStyle.Dash) {
    return (
      <li className={cn('flex items-start', spacingClasses)}>
        <span className="mr-2 text-content-primary flex-shrink-0">–</span>
        <span>{children}</span>
      </li>
    );
  }

  const isCheckmark = bulletStyle === BulletListStyle.Checkmark;
  const bulletClasses = isCheckmark ? 'checkmark-bullet text-fern mt-1 -mr-1' : 'arrow-right';
  return (
    <li className={cn('gap-[10px]', spacingClasses)}>
      <span
        className={cn(
          bulletClasses,
          bulletStyle === BulletListStyle.Arrow && arrowStyles, // Only apply arrowStyles for arrows
        )}>
        {isCheckmark ? <CheckmarkHeavy size={14} /> : <ArrowRight />}
      </span>
      {children}
    </li>
  );
};
